import { useCallback, useMemo } from 'react';

import { useChatStream, type ChatStreamMessageSenderConfig } from './chat';

interface UseGatewayChatStreamArgs {
  gatewayApiBase: string;
  ensureSession: () => Promise<boolean>;
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
  config?: GatewayChatStreamConfig;
}

interface GatewayChatStreamContext {
  gatewayApiBase: string;
}

export interface GatewayChatStreamConfig {
  buildStreamUrl?: (context: GatewayChatStreamContext, conversationId: string) => string;
  buildExecuteUrl?: (context: GatewayChatStreamContext, conversationId: string) => string;
  messageSender?: ChatStreamMessageSenderConfig;
}

export const useGatewayChatStream = ({
  gatewayApiBase,
  ensureSession,
  authenticatedFetch,
  config,
}: UseGatewayChatStreamArgs) => {
  const context = useMemo<GatewayChatStreamContext>(() => ({ gatewayApiBase }), [gatewayApiBase]);

  const buildStreamUrl = useCallback(
    (conversationId: string) =>
      config?.buildStreamUrl
        ? config.buildStreamUrl(context, conversationId)
        : `${context.gatewayApiBase}/stream?conversation_id=${conversationId}`,
    [config?.buildStreamUrl, context]
  );

  const buildExecuteUrl = useCallback(
    (conversationId: string) =>
      config?.buildExecuteUrl
        ? config.buildExecuteUrl(context, conversationId)
        : `${context.gatewayApiBase}/execute?conversation_id=${conversationId}`,
    [config?.buildExecuteUrl, context]
  );

  return useChatStream({
    ensureSession,
    authenticatedFetch,
    buildStreamUrl,
    buildExecuteUrl,
    messageSenderConfig: config?.messageSender,
  });
};
